// src/App.js
import { useEffect, useState } from "react";
import { useUser, useAuth } from "@clerk/clerk-react";
import { createApiClient } from "./apiClient";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Auth from "./components/Auth";
import HomeDashboard from "./pages/HomeDashboard";
import AddActivity from "./pages/AddActivity";
import AddActivityCalendar from "./pages/AddActivityCalendar";
import CalendarView from "./pages/CalendarView";
import Infographic from "./pages/Infographic";
import Navbar from "./components/Navbar";
import BottomNav from "./components/BottomNav";
import More from "./pages/More";
import Subscribe from "./pages/Subscribe";
import { Toaster } from "react-hot-toast";
import { LanguageProvider } from "./contexts/LanguageContext";
import WelcomePage from "./pages/WelcomePage";
import { TimerProvider } from "./contexts/TimerContext";
import MiniTimerBar from "./components/MiniTimerBar";
import LifeCountdownBar from "./components/LifeCountdownBar";

// --- บังคับ redirect ถ้าเข้า dev domain ---
if (window.location.hostname === "spendora.pages.dev") {
  window.location.href =
    "https://spendora.thatwhy.me" +
    window.location.pathname +
    window.location.search +
    window.location.hash;
}

// --- scroll ไปบนสุดอัตโนมัติเมื่อเปลี่ยน route (UX) ---
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const { isLoaded, isSignedIn, user: clerkUser } = useUser();
  const { getToken } = useAuth();
  const [userProfile, setUserProfile] = useState(null);

  const currentAge = userProfile?.current_age ? Number(userProfile.current_age) : null;
  const targetAge = userProfile?.target_age ? Number(userProfile.target_age) : null;

  // โหลด profile จาก Worker เมื่อ login (Worker auto-create ถ้ายังไม่มี)
  useEffect(() => {
    if (!isSignedIn || !getToken) { setUserProfile(null); return; }
    createApiClient(getToken).profile.get()
      .then(setUserProfile)
      .catch(() => setUserProfile(null));
  }, [isSignedIn, getToken]);

  // OneSignal push notification
  useEffect(() => {
    if (!window._onesignalInitialized) {
      if (!window.OneSignal) {
        const script = document.createElement("script");
        script.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
        script.async = true;
        document.body.appendChild(script);
        script.onload = () => {
          if (!window._onesignalInitialized && window.OneSignal) initOneSignal();
        };
      } else {
        if (!window._onesignalInitialized) initOneSignal();
      }
    }
    function initOneSignal() {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(async function (OneSignal) {
        await OneSignal.init({
          appId: "45e18ec2-8a49-4fba-9c79-bf8b382ea350",
          autoResubscribe: true,
          notifyButton: { enable: false },
        });
        window._onesignalInitialized = true;
      });
    }
  }, []);

  if (!isLoaded) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-6"></div>
        <span className="text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <TimerProvider>
        <Router>
          <ScrollToTop />
          <Toaster position="bottom-center" />
          <MiniTimerBar />

          <Routes>
            <Route path="/welcome" element={<WelcomePage userProfile={userProfile} user={clerkUser} />} />

            {!isSignedIn && (
              <Route path="*" element={<Auth />} />
            )}

            {isSignedIn && (
              <Route
                path="*"
                element={
                  <>
                    <Navbar userProfile={userProfile} user={clerkUser} />
                    <LifeCountdownBar
                      currentAge={currentAge}
                      targetAge={targetAge}
                      loading={!isLoaded}
                    />
                    <BottomNav userProfile={userProfile} user={clerkUser} />
                    <div className="pt-[90px] pb-20">
                      <Routes>
                        <Route path="/" element={<HomeDashboard userProfile={userProfile} user={clerkUser} />} />
                        <Route path="/add" element={<AddActivity userProfile={userProfile} user={clerkUser} />} />
                        <Route path="/infographic" element={<Infographic userProfile={userProfile} user={clerkUser} />} />
                        <Route path="/add-calendar" element={<AddActivityCalendar userProfile={userProfile} user={clerkUser} />} />
                        <Route path="/calendar" element={<CalendarView userProfile={userProfile} user={clerkUser} />} />
                        <Route path="/auth" element={<Navigate to="/" />} />
                        <Route path="/more" element={<More userProfile={userProfile} user={clerkUser} />} />
                        <Route path="/subscribe" element={<Subscribe userProfile={userProfile} user={clerkUser} />} />
                      </Routes>
                    </div>
                  </>
                }
              />
            )}
          </Routes>
        </Router>
      </TimerProvider>
    </LanguageProvider>
  );
}